import React, { useState, useEffect, useRef } from 'react';
import logo from '../../../assets/images/brand/LOGO KENT.png';
import { ReactComponent as FacebookIcon } from '../../../assets/images/common/Facebook_icon.svg';
import { ReactComponent as ThreadsLogo } from '../../../assets/images/common/Threads_logo.svg';
import { useLanguage } from '../../../contexts/LanguageContext';

// ── Animated number counter ───────────────────────────────────────────────────
function CountUp({ target, duration = 1200 }) {
  const { language } = useLanguage();
  const [value, setValue] = useState(0);
  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!target) return;
    startRef.current = performance.now();

    const step = (now) => {
      const elapsed = now - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return <>{value.toLocaleString(language === 'vie' ? 'vi-VN' : 'en-US')}</>;
}
// ─────────────────────────────────────────────────────────────────────────────

function Footer() {
  const { t } = useLanguage();
  const [visitorStats, setVisitorStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch('/api/visitor-stats');
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        setVisitorStats(data);
      } catch {
        setVisitorStats(null);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, []);

  return (
    <footer className="mt-20 bg-black p-8 shadow-xl w-full">
      {/* Layer 1: Left (Logo + Intro + Social) and Right (Contact + Stats) */}
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
        {/* Left Side: Logo + Introduction + Social Icons */}
        <div className="text-left">
          <img
            src={logo}
            alt="KenT Logo"
            className="h-16 md:h-20 object-contain mx-0 mb-4"
          />
          <p className="text-gray-300 text-sm mb-4">
            {t('footer.description')}
          </p>
          {/* Social Icons below Introduction */}
          <div className="flex gap-3 justify-start">
            <a href="https://www.facebook.com/profile.php?id=61584103769354" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#3b5998] rounded-full flex items-center justify-center hover:scale-110 transition">
              <FacebookIcon className="w-5 h-5" />
            </a>
            <a href="https://www.threads.net/@_mt.std_" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#000000] rounded-full flex items-center justify-center hover:scale-110 transition">
              <ThreadsLogo className="w-5 h-5" />
            </a>
            <a href="https://www.instagram.com/_mt.std_/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] rounded-full flex items-center justify-center hover:scale-110 transition">
              <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/instagram.svg" alt="IG" className="w-5 h-5 invert" />
            </a>
          </div>
        </div>

        {/* Right Side: Contact + Visitor Stats */}
        <div className="text-left md:text-right">
          {/* Contact */}
          <h3 className="text-xl font-bold text-white mb-4">{t('footer.contact')}</h3>
          <div className="space-y-2 mb-8">
            <a href="mailto:bomthanh10@gmail.com" className="block text-gray-300 hover:text-pink-400 transition">
              📧 bomthanh10@gmail.com
            </a>
            <a href="tel:0334704210" className="block text-gray-300 hover:text-pink-400 transition">
              📱 0334704210
            </a>
          </div>

          {/* Visitor Stats */}
          <p className="text-gray-400 text-sm mt-2">
            {t('footer.total_visits')}{' '}
            <span className="text-white font-semibold">
              {statsLoading ? '...' : visitorStats ? <CountUp target={visitorStats.totalClicks} /> : '—'}
            </span>
          </p>
        </div>
      </div>

      {/* Layer 2: Copyright */}
      <div className="max-w-6xl mx-auto mt-8 pt-8 border-t border-gray-800">
        <p className="text-center text-gray-400 text-sm">
          © 2026 KenT Photobooth by KenT Software. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

export default Footer;
